import { useEffect, useRef, useState } from "react";
import { Globe, Upload, MapPin, RefreshCcw, TrendingUp } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api, API } from "@/lib/api";
import { LANGS } from "@/lib/i18n";
import { toast } from "sonner";

// key, label, multilingual?, big?, group
const FIELDS = [
  ["site_name", "Site name", false, false, "General"],
  ["tagline", "Tagline", true, false, "General"],
  ["timezone", "Timezone (IANA, e.g. Europe/Tirane)", false, false, "General"],
  ["copyright_text", "Footer copyright text", false, false, "General"],
  ["maintenance_mode", "Maintenance mode (true/false — hides public site)", false, false, "General"],
  ["maintenance_message", "Maintenance message (shown to visitors)", false, true, "General"],
  ["cookie_banner_enabled", "Cookie banner enabled (true/false)", false, false, "General"],
  ["cookie_banner_text", "Cookie banner text", true, true, "General"],

  ["hero_title", "Hero title", true, false, "Branding"],
  ["hero_subtitle", "Hero subtitle", true, false, "Branding"],
  ["about_text", "About text", true, true, "Branding"],
  ["favicon_url", "Favicon URL (.ico, .png)", false, false, "Branding"],

  ["seo_title", "SEO title", true, false, "SEO"],
  ["seo_description", "SEO description", true, true, "SEO"],
  ["ga4_id", "Google Analytics ID (G-XXXX)", false, false, "SEO"],

  ["address", "Address", false, false, "Location"],
  ["lat", "Latitude (e.g. 39.8754)", false, false, "Location"],
  ["lng", "Longitude (e.g. 20.0050)", false, false, "Location"],
  ["maps_embed_url", "Google Maps embed URL", false, false, "Location"],

  ["phone", "Reception phone", false, false, "Contact"],
  ["email", "Reception email", false, false, "Contact"],
  ["whatsapp", "WhatsApp number (digits only, e.g. 355690000000)", false, false, "Contact"],
  ["instagram", "Instagram URL", false, false, "Contact"],
  ["facebook", "Facebook URL", false, false, "Contact"],

  ["support_phone", "Support phone", false, false, "Support"],
  ["support_email", "Support email", false, false, "Support"],
  ["safety_emergency", "Safety / emergency numbers", false, true, "Support"],

  ["currency_default", "Default currency (EUR, USD, GBP, ALL)", false, false, "Currency"],
  ["currencies_enabled", "Enabled currencies (comma-separated)", false, false, "Currency"],
  ["fx_rates", 'FX rates JSON e.g. {"EUR":1,"USD":1.08,"GBP":0.85,"ALL":99.5}', false, true, "Currency"],
];

const GROUPS = ["General", "Branding", "Location", "Contact", "Support", "Currency", "SEO"];

export default function StaffSettingsPage() {
  const [vals, setVals] = useState({});
  const [lang, setLang] = useState("en");
  const [uploading, setUploading] = useState(false);
  const [refreshingFx, setRefreshingFx] = useState(false);
  const [fxStatus, setFxStatus] = useState(null);
  const fileRef = useRef(null);

  function load() {
    api.get("/settings").then((r) => setVals(r.data));
    api.get("/fx/status").then((r) => setFxStatus(r.data)).catch(() => {});
  }
  useEffect(load, []);

  async function refreshFx() {
    setRefreshingFx(true);
    try {
      const r = await api.post("/admin/fx/refresh");
      toast.success(`Rates updated · ${Object.keys(r.data.rates).length} currencies`);
      // Also save rates back into the form view so user sees the new JSON
      setVals((prev) => ({
        ...prev,
        fx_rates: { ...(prev.fx_rates || {}), value: JSON.stringify(r.data.rates) },
      }));
      setFxStatus({ rates: r.data.rates, updated_at: r.data.updated_at, provider_time: r.data.provider_time });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Live FX provider error — try again later");
    } finally {
      setRefreshingFx(false);
    }
  }

  function get(key) {
    const item = vals[key];
    if (!item) return "";
    if (lang === "en") return item.value ?? "";
    return item.i18n?.[lang] ?? "";
  }

  function set(key, v) {
    const item = vals[key] || { value: "", i18n: {} };
    if (lang === "en") {
      setVals({ ...vals, [key]: { ...item, value: v } });
    } else {
      const i18n = { ...(item.i18n || {}), [lang]: v };
      setVals({ ...vals, [key]: { ...item, i18n } });
    }
  }

  async function save(key) {
    const item = vals[key] || { value: "", i18n: {} };
    await api.put(`/settings/${key}`, { key, value: item.value || "", i18n: item.i18n || {} });
    toast.success("Saved");
  }

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      // Refresh settings so we see the new URL
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const logoUrlRaw = vals?.logo_url?.value;
  const logoSrc = logoUrlRaw && logoUrlRaw.trim()
    ? (logoUrlRaw.startsWith("http") ? logoUrlRaw : `${API}${logoUrlRaw.startsWith("/api") ? logoUrlRaw.slice(4) : logoUrlRaw}`)
    : null;

  return (
    <StaffLayout>
      <h1 className="font-display text-4xl">Site settings</h1>
      <p className="text-sm text-[#71717A] mt-1">Everything on the public site — editable, multilingual, no code.</p>

      {/* Logo upload */}
      <div className="mt-8 bg-white border border-[#E4E4E7] rounded-md p-6 max-w-3xl">
        <h2 className="font-display text-2xl">Logo</h2>
        <p className="text-xs text-[#71717A] mt-1">Upload a new logo any time. Used on navbar, footer, login & emails.</p>
        <div className="mt-4 flex items-center gap-6 flex-wrap">
          <div className="w-32 h-32 border border-[#E4E4E7] bg-[#FAFAFA] rounded-md flex items-center justify-center overflow-hidden">
            {logoSrc ? (
              <img src={logoSrc} alt="Logo" className="max-w-full max-h-full" data-testid="settings-logo-preview" />
            ) : (
              <span className="text-xs text-[#71717A]">Using default</span>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="settings-logo-file" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-brand-primary text-white px-4 py-2.5 rounded-sm text-sm">
              <Upload size={14} /> {uploading ? "Uploading…" : "Upload new logo"}
            </button>
            {logoSrc && (
              <button onClick={async () => { await api.put("/settings/logo_url", { key: "logo_url", value: "" }); toast.success("Reset to default"); load(); }}
                className="ml-3 text-xs text-[#71717A] hover:text-brand-text">Reset to default</button>
            )}
            <div className="text-[10px] text-[#71717A] mt-2">PNG, JPG, WebP or SVG, transparent background recommended.</div>
          </div>
        </div>
      </div>

      {/* Lang switcher */}
      <div className="mt-6 flex items-center gap-2 bg-white border border-[#E4E4E7] rounded-md px-4 py-2 w-fit">
        <Globe size={14} className="text-brand-muted" />
        {LANGS.map((l) => (
          <button
            key={l.code}
            data-testid={`settings-lang-${l.code}`}
            onClick={() => setLang(l.code)}
            className={`px-3 py-1 text-xs uppercase tracking-wider rounded-sm ${lang === l.code ? "bg-brand-primary text-white" : "text-[#71717A] hover:bg-[#FAFAFA]"}`}
          >
            {l.flag} {l.code}
          </button>
        ))}
      </div>

      {GROUPS.map((g) => {
        const groupFields = FIELDS.filter(([_, __, ___, ____, group]) => group === g);
        return (
          <div key={g} className="mt-6 bg-white border border-[#E4E4E7] rounded-md p-6 space-y-5 max-w-3xl">
            <h2 className="font-display text-2xl">{g}</h2>

            {g === "Currency" && (
              <div data-testid="fx-live-card" className="border border-brand-primary/20 bg-brand-secondary/40 rounded-md p-4 -mt-1">
                <div className="flex items-start gap-3">
                  <TrendingUp size={18} className="text-brand-primary mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">Live FX rates from open.er-api.com</div>
                    <div className="text-xs text-[#71717A] mt-0.5">
                      {fxStatus?.updated_at
                        ? <>Last updated <strong>{new Date(fxStatus.updated_at).toLocaleString()}</strong></>
                        : "Never refreshed — click below to fetch live market rates."}
                    </div>
                    {fxStatus?.rates && (
                      <div className="mt-3 flex flex-wrap gap-3 text-xs">
                        {Object.entries(fxStatus.rates).map(([c, v]) => (
                          <span key={c} className="px-2.5 py-1 bg-white rounded-sm border border-[#E4E4E7]">
                            <strong className="uppercase">{c}</strong>: <span className="font-mono">{Number(v).toFixed(4)}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    data-testid="fx-refresh-btn"
                    onClick={refreshFx}
                    disabled={refreshingFx}
                    className="shrink-0 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-50 text-white px-4 py-2 rounded-sm text-xs"
                  >
                    <RefreshCcw size={12} className={refreshingFx ? "animate-spin" : ""} /> {refreshingFx ? "Refreshing…" : "Refresh now"}
                  </button>
                </div>
                <div className="mt-3 text-[10px] text-[#71717A]">
                  Auto-refreshes every 12 hours · Free provider, no key required · Filtered to your enabled currencies
                </div>
              </div>
            )}

            {groupFields.map(([k, label, multi, big]) => {
              const showInLang = multi || lang === "en";
              if (!showInLang) return null;
              return (
                <div key={k}>
                  <label className="block">
                    <span className="text-xs uppercase tracking-wider text-[#71717A]">
                      {label} {multi && lang !== "en" ? `(${lang})` : ""}
                    </span>
                    {big ? (
                      <textarea data-testid={`setting-${k}`} rows={k === "fx_rates" ? 3 : 5} value={get(k)} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm font-mono" />
                    ) : (
                      <input data-testid={`setting-${k}`} value={get(k)} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                    )}
                  </label>
                  <button data-testid={`setting-save-${k}`} onClick={() => save(k)} className="mt-2 text-xs text-brand-primary hover:text-brand-primaryHover">Save</button>
                </div>
              );
            })}

            {g === "Location" && (
              <div className="mt-2 border-t border-[#E4E4E7] pt-4">
                <div className="text-xs uppercase tracking-wider text-[#71717A] flex items-center gap-1.5 mb-2"><MapPin size={12} /> Map preview</div>
                {vals?.maps_embed_url?.value ? (
                  <iframe
                    title="Map preview"
                    src={vals.maps_embed_url.value}
                    className="w-full h-56 rounded-md border border-[#E4E4E7]"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-56 rounded-md border border-dashed border-[#E4E4E7] flex items-center justify-center text-xs text-[#71717A]">
                    Add a Google Maps embed URL above to see a preview here.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </StaffLayout>
  );
}
